import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Users, Plus, Trash2, KeyRound, ShieldCheck, UserCog, Eye, Copy, X, Search, Check, Pencil,
    Building2, Briefcase, Loader2, CopyPlus, Headphones, Phone, Globe, Info, Download,
} from 'lucide-react';
import { useTenant } from '../components/TenantContext.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { membersApi } from '../api/members.js';
import { tenantsApi } from '../api/tenants.js';
import { accountsApi } from '../api/accounts.js';
import { serviceAgentsApi } from '../api/serviceAgents.js';
import { resolveFlowMode } from '../api/ivrAgents.js';
import { copyText } from '../lib/clipboard.js';
import { useAvatarsList } from '../hooks/useAvatarsList.js';
import { CreateAccountModal } from '../components/SuperAdmin/CreateAccountModal.jsx';
import { MonthPager, currentMonthTaipei } from '../components/SuperAdmin/MonthPager.jsx';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';

const ROLE_META = {
    admin: { labelKey: 'roles.admin.label', descKey: 'roles.admin.desc', icon: ShieldCheck, color: 'text-accent-700 bg-accent-50' },
    agent: { labelKey: 'roles.agent.label', descKey: 'roles.agent.desc', icon: UserCog, color: 'text-success-500 bg-success-100' },
    viewer: { labelKey: 'roles.viewer.label', descKey: 'roles.viewer.desc', icon: Eye, color: 'text-ink-500 bg-cream-100' },
};

// 母帳號角色從 admin-console 不可建（只能 SaaS 層建）
const CREATABLE_ROLES = ['agent', 'viewer'];

/**
 * 帳號管理頁殼層：
 *   - 經銷商（dealer）→「商用帳號 / 子帳號 / 真人客服」
 *   - 其他母帳號（business 等）→「子帳號 / 真人客服」
 * 真人客服 = 登入「Qubby 客服台」extension 接手 AI 轉真人的帳號（≠ 子帳號的 agent role）。
 */
export default function MembersPage() {
    const { t } = useTranslation('members');
    const { accountRole } = useTenant();
    const isDealer = accountRole === 'dealer';
    const [tab, setTab] = useState(isDealer ? 'business' : 'sub');

    const tabs = [
        ...(isDealer ? [{ k: 'business', label: t('members.tabBusiness'), icon: Building2 }] : []),
        { k: 'sub', label: t('members.tabSub'), icon: Users },
        { k: 'agents', label: t('members.tabAgents'), icon: Headphones },
    ];

    return (
        <div className="max-w-4xl">
            <div className="mb-5 inline-flex rounded-lg border border-cream-200 bg-cream-100 p-1">
                {tabs.map(({ k, label, icon: Icon }) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={[
                            'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition',
                            tab === k ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700',
                        ].join(' ')}
                    >
                        <Icon size={14} /> {label}
                    </button>
                ))}
            </div>
            {tab === 'sub' ? <SubAccountsTab /> : tab === 'agents' ? <ServiceAgentsTab /> : <BusinessAccountsTab />}
        </div>
    );
}

function SubAccountsTab() {
    const { t } = useTranslation('members');
    const { hostId, currentTenant, isAdmin } = useTenant();
    const { user } = useAuth();

    const [list, setList] = useState([]);
    const [quota, setQuota] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showOTP, setShowOTP] = useState(null);   // { email, password }
    const [notice, setNotice] = useState(null);     // { email, renamedFrom } — email 本來就有帳號

    async function load() {
        if (!hostId) return;
        setLoading(true);
        try {
            const [m, q] = await Promise.all([
                membersApi.list(hostId),
                tenantsApi.quota(hostId).catch(() => null),
            ]);
            setList(m.members || []);
            setQuota(q || null);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    if (!isAdmin) {
        return (
            <Card>
                <Empty
                    icon={Users}
                    title={t('members.adminOnlyTitle')}
                    description={t('members.adminOnlyDesc')}
                />
            </Card>
        );
    }

    const subAccountCount = list.filter((m) => m.role !== 'admin').length;
    return (
        <div className="max-w-4xl">
            <PageHeader
                eyebrow={`tenant · ${hostId}`}
                title={t('members.title')}
                description={t('members.description', { tenant: currentTenant?.displayName || hostId })}
                actions={
                    <Button variant="accent" onClick={() => setShowCreate(true)}>
                        <Plus size={14} /> {t('members.addSubAccount')}
                    </Button>
                }
            />

            <div className="mb-6">
                <QuotaPill
                    label={t('members.subAccount')}
                    used={subAccountCount}
                    total={quota?.subAccountQuota}
                    fallback={t('members.totalMembers', { n: list.length })}
                />
            </div>

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {notice && (
                <Card className="bg-accent-50 border-accent-500/30 mb-4">
                    <div className="flex items-start gap-2">
                        <Info size={15} className="text-accent-700 shrink-0 mt-0.5" />
                        <div className="text-sm text-ink-700">
                            <div>{t('members.existingAccountNotice', { email: notice.email })}</div>
                            {notice.renamedFrom && (
                                <div className="mt-1">
                                    {t('members.existingAccountRenamed', { from: notice.renamedFrom })}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setNotice(null)}
                            className="ml-auto text-ink-400 hover:text-ink-700 shrink-0"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </Card>
            )}

            {showCreate && (
                <CreateMemberDialog
                    hostId={hostId}
                    onClose={() => setShowCreate(false)}
                    onCreated={(result) => {
                        setShowCreate(false);
                        load();
                        // 這個 email 本來就有帳號 → 沿用既有帳號（不會發新密碼），明確告知操作者
                        setNotice(
                            result.existingAccount
                                ? { email: result.member.email, renamedFrom: result.renamedFrom || null }
                                : null
                        );
                        if (result.oneTimePassword) {
                            setShowOTP({
                                email: result.member.email,
                                password: result.oneTimePassword,
                            });
                        }
                    }}
                />
            )}

            {showOTP && <OneTimePasswordDialog data={showOTP} onClose={() => setShowOTP(null)} />}

            {loading ? (
                <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>
            ) : list.length === 0 ? (
                <Card>
                    <Empty
                        icon={Users}
                        title={t('members.emptyTitle')}
                        description={t('members.emptyDesc')}
                        action={
                            <Button variant="accent" onClick={() => setShowCreate(true)}>
                                <Plus size={14} /> {t('members.addSubAccount')}
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <div className="grid gap-2">
                    {list.map((m) => (
                        <MemberRow
                            key={m.uid}
                            member={m}
                            isSelf={m.uid === user?.uid}
                            onChange={load}
                            hostId={hostId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function assignmentSummary(member, t) {
    const n = Array.isArray(member.assignedAvatarIds) ? member.assignedAvatarIds.length : 0;
    return n === 0 ? t('members.noAssignment') : t('members.assignedCount', { n });
}

function MemberRow({ member, isSelf, onChange, hostId }) {
    const { t } = useTranslation('members');
    const meta = ROLE_META[member.role] || ROLE_META.viewer;
    const Icon = meta.icon;
    const [editAssign, setEditAssign] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState(member.displayName || '');
    const [savingName, setSavingName] = useState(false);

    async function saveName() {
        const trimmed = nameDraft.trim();
        if (!trimmed || trimmed === member.displayName) { setEditingName(false); return; }
        setSavingName(true);
        try {
            await membersApi.update(hostId, member.uid, { displayName: trimmed });
            setEditingName(false);
            onChange();
        } catch (e) {
            alert(e.message);
        } finally {
            setSavingName(false);
        }
    }

    async function changeRole(role) {
        try {
            await membersApi.update(hostId, member.uid, { role });
            onChange();
        } catch (e) {
            alert(e.message);
        }
    }

    async function remove() {
        if (!confirm(t('members.removeConfirm', { email: member.email }))) return;
        try {
            await membersApi.remove(hostId, member.uid);
            onChange();
        } catch (e) {
            alert(e.message);
        }
    }

    async function resetPassword() {
        if (!confirm(t('members.resetPwConfirm', { email: member.email }))) return;
        try {
            const r = await membersApi.resetPassword(hostId, member.uid);
            // v1: 直接顯示 link 給 admin 自己貼給對方；v2 接 SES 寄信
            prompt(t('members.resetPwPrompt'), r.resetLink);
        } catch (e) {
            alert(e.message);
        }
    }

    return (
        <Card className="flex items-center gap-4">
            <div className={['size-10 rounded-md grid place-items-center shrink-0', meta.color].join(' ')}>
                <Icon size={18} strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {editingName ? (
                        <span className="inline-flex items-center gap-1">
                            <input
                                autoFocus
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveName();
                                    else if (e.key === 'Escape') { setEditingName(false); setNameDraft(member.displayName || ''); }
                                }}
                                disabled={savingName}
                                placeholder={t('members.displayName')}
                                className="font-medium text-ink-900 bg-white border border-accent-400 rounded px-2 py-0.5 outline-none focus:border-accent-500 min-w-[160px]"
                            />
                            <button onClick={saveName} disabled={savingName} title={t('members.saveEnter')}
                                className="size-6 inline-flex items-center justify-center rounded text-success-500 hover:bg-cream-100">
                                <Check size={13} strokeWidth={2.2} />
                            </button>
                            <button onClick={() => { setEditingName(false); setNameDraft(member.displayName || ''); }} disabled={savingName} title={t('members.cancelEsc')}
                                className="size-6 inline-flex items-center justify-center rounded text-ink-400 hover:bg-cream-100">
                                <X size={13} strokeWidth={2.2} />
                            </button>
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 group/name">
                            <span className="font-medium text-ink-900">
                                {member.displayName || member.email}
                            </span>
                            <button
                                onClick={() => { setNameDraft(member.displayName || ''); setEditingName(true); }}
                                title={t('members.editDisplayName')}
                                className="size-6 inline-flex items-center justify-center rounded text-ink-300 hover:text-accent-700 hover:bg-cream-100 transition"
                            >
                                <Pencil size={12} strokeWidth={1.8} />
                            </button>
                        </span>
                    )}
                    {isSelf && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-cream-200 rounded text-ink-500">
                            {t('members.you')}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-ink-500">{member.email}</span>
                    <span className="text-[10px] font-mono text-ink-300">{member.uid.slice(0, 12)}…</span>
                </div>
                <div className="text-[11px] text-ink-400 mt-1">{t(meta.descKey)}</div>
                {member.role !== 'admin' && (
                    <div className="text-[11px] mt-1">
                        <span className="text-ink-400">{t('members.visibleRoles')}</span>
                        <span className={!(member.assignedAvatarIds || []).length ? 'text-warn-500' : 'text-ink-600'}>
                            {assignmentSummary(member, t)}
                        </span>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-1">
                {!isSelf && member.role !== 'admin' && (
                    <>
                        <select
                            value={member.role}
                            onChange={(e) => changeRole(e.target.value)}
                            className="px-2 py-1 text-xs bg-white border border-cream-300 rounded text-ink-700 focus:outline-none focus:border-accent-500"
                        >
                            <option value="agent">{t('roles.agent.label')}</option>
                            <option value="viewer">{t('roles.viewer.label')}</option>
                        </select>
                        <Button size="sm" variant="ghost" onClick={() => setEditAssign(true)} title={t('members.editVisibleRoles')}>
                            {t('members.assign')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={resetPassword} title={t('members.sendResetEmail')}>
                            <KeyRound size={13} className="text-ink-500" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={remove} title={t('common:actions.delete')}>
                            <Trash2 size={13} className="text-danger-500" />
                        </Button>
                        {editAssign && (
                            <EditAssignmentDialog
                                hostId={hostId}
                                member={member}
                                onClose={() => setEditAssign(false)}
                                onSaved={() => { setEditAssign(false); onChange(); }}
                            />
                        )}
                    </>
                )}
                {!isSelf && member.role === 'admin' && (
                    <span className="text-[10px] text-ink-400 font-mono uppercase tracking-wider">
                        {t('members.adminManagedBySaas')}
                    </span>
                )}
            </div>
        </Card>
    );
}

/** 可見 AI 角色指派器 —— 搜尋 + 多選(可撐 100+ avatar)。子帳號只能挑指定 avatar,沒有「全部」。 */
function AvatarAssignment({ hostId, assignedAvatarIds, onChange }) {
    const { t } = useTranslation('members');
    const { avatars, loading } = useAvatarsList(hostId);
    const [query, setQuery] = useState('');
    const selected = new Set(assignedAvatarIds || []);

    // 字母排序、依搜尋過濾（清單穩定不跳動；已選靠上方 chips 找）
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (avatars || [])
            .filter((a) => !q || (a.name || '').toLowerCase().includes(q))
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'zh-TW'));
    }, [avatars, query]);

    const selectedAvatars = (avatars || []).filter((a) => selected.has(a.avatarId));

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        onChange([...next]);
    };
    const selectAllFiltered = () => {
        const next = new Set(selected);
        filtered.forEach((a) => next.add(a.avatarId));
        onChange([...next]);
    };
    const clearAll = () => onChange([]);

    return (
        <div>
            <span className="block text-xs font-medium text-ink-700 mb-2 uppercase tracking-wide">
                {t('members.viewableRoles')}
            </span>

            {/* 已選 chips */}
            {selectedAvatars.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2 max-h-24 overflow-y-auto">
                    {selectedAvatars.map((a) => (
                                <span
                                    key={a.avatarId}
                                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-accent-50 border border-accent-200 text-[12px] text-accent-800"
                                >
                                    <span className="truncate max-w-[160px]">{a.name || t('members.unnamedAvatar')}</span>
                                    <button
                                        type="button"
                                        onClick={() => toggle(a.avatarId)}
                                        className="text-accent-500 hover:text-danger-500"
                                        aria-label={t('common:actions.delete')}
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* 搜尋 */}
                    <div className="relative mb-2">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('members.searchAvatarPlaceholder')}
                            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-cream-300 rounded-md focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                        />
                    </div>

                    {/* 計數 + 動作 */}
                    <div className="flex items-center justify-between mb-1.5 px-0.5">
                        <span className="text-[11px] text-ink-400">
                            {t('members.selected')} <span className="text-ink-900 font-medium tnum">{selected.size}</span>
                            <span className="text-ink-300"> {t('members.showingCount', { shown: filtered.length, total: (avatars || []).length })}</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <button type="button" onClick={selectAllFiltered} className="text-[11px] text-accent-700 hover:underline">
                                {query ? t('members.selectAllFiltered') : t('members.selectAll')}
                            </button>
                            <span className="text-cream-300">|</span>
                            <button type="button" onClick={clearAll} className="text-[11px] text-ink-500 hover:text-danger-500 hover:underline">
                                {t('members.clear')}
                            </button>
                        </span>
                    </div>

                    {/* 清單 */}
                    <div className="border border-cream-300 rounded-md max-h-60 overflow-y-auto divide-y divide-cream-100">
                        {loading ? (
                            <div className="px-3 py-6 text-center text-xs text-ink-400">{t('members.loadingAvatars')}</div>
                        ) : filtered.length === 0 ? (
                            <div className="px-3 py-6 text-center text-xs text-ink-400">
                                {(avatars || []).length === 0 ? t('members.noAvatarsInTenant') : t('members.noMatchingAvatars')}
                            </div>
                        ) : filtered.map((a) => {
                            const on = selected.has(a.avatarId);
                            return (
                                <button
                                    key={a.avatarId}
                                    type="button"
                                    onClick={() => toggle(a.avatarId)}
                                    className={[
                                        'w-full flex items-center gap-2.5 px-3 py-2 text-left transition',
                                        on ? 'bg-accent-50' : 'hover:bg-cream-50',
                                    ].join(' ')}
                                >
                                    <span className={[
                                        'size-4 rounded border grid place-items-center shrink-0',
                                        on ? 'bg-accent-700 border-accent-700 text-white' : 'border-cream-300 bg-white',
                                    ].join(' ')}>
                                        {on && <Check size={12} strokeWidth={3} />}
                                    </span>
                                    <span className={['text-sm truncate', on ? 'text-ink-900 font-medium' : 'text-ink-700'].join(' ')}>
                                        {a.name || t('members.unnamedAvatar')}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
            {selected.size === 0 && (
                <div className="text-[11px] text-warn-500 mt-1.5">{t('members.noSelectionWarning')}</div>
            )}
        </div>
    );
}

function EditAssignmentDialog({ hostId, member, onClose, onSaved }) {
    const { t } = useTranslation('members');
    const [assignedAvatarIds, setAssignedAvatarIds] = useState(member.assignedAvatarIds || []);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function save() {
        setSubmitting(true);
        setError(null);
        try {
            await membersApi.update(hostId, member.uid, {
                assignedAllAvatars: false,
                assignedAvatarIds,
            });
            onSaved();
        } catch (e) {
            setError(e);
            setSubmitting(false);
        }
    }

    return (
        <Modal onClose={onClose} title={t('members.assignDialogTitle', { name: member.displayName || member.email })} wide>
            <div className="space-y-4">
                <AvatarAssignment
                    hostId={hostId}
                    assignedAvatarIds={assignedAvatarIds}
                    onChange={setAssignedAvatarIds}
                />
                {error && (
                    <div className="text-sm text-danger-500 bg-danger-100/60 border border-danger-500/30 px-3 py-2 rounded-md">
                        {error.message}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
                    <Button type="button" variant="accent" disabled={submitting} onClick={save}>
                        {submitting ? t('members.saving') : t('common:actions.save')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function CreateMemberDialog({ hostId, onClose, onCreated }) {
    const { t } = useTranslation('members');
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [role, setRole] = useState('agent');
    // 新子帳號一開始什麼角色都看不到,由母帳號明確指派
    const [assignedAvatarIds, setAssignedAvatarIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const r = await membersApi.create(hostId, {
                email,
                displayName,
                role,
                assignedAllAvatars: false,
                assignedAvatarIds,
            });
            onCreated(r);
        } catch (e) {
            setError(e);
            setSubmitting(false);
        }
    }

    return (
        <Modal onClose={onClose} title={t('members.addSubAccount')} wide>
            <form onSubmit={submit} className="space-y-4">
                <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="agent@your-company.com"
                    required
                    hint={t('members.emailHint')}
                />
                <Input
                    label={t('members.displayName')}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t('members.displayNamePlaceholder')}
                />
                <div>
                    <span className="block text-xs font-medium text-ink-700 mb-1.5 uppercase tracking-wide">
                        {t('members.role')}
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                        {CREATABLE_ROLES.map((k) => {
                            const v = ROLE_META[k];
                            return (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => setRole(k)}
                                    className={[
                                        'px-3 py-2 rounded-md border text-left transition',
                                        role === k
                                            ? 'border-accent-500 bg-accent-50'
                                            : 'border-cream-300 bg-white hover:bg-cream-100',
                                    ].join(' ')}
                                >
                                    <div className="text-sm font-medium text-ink-900">{t(v.labelKey)}</div>
                                    <div className="text-[10px] text-ink-500 mt-0.5">{t(v.descKey)}</div>
                                </button>
                            );
                        })}
                    </div>
                    <div className="text-[11px] text-ink-400 mt-2">
                        {t('members.extraAdminNote')}
                    </div>
                </div>

                <AvatarAssignment
                    hostId={hostId}
                    assignedAvatarIds={assignedAvatarIds}
                    onChange={setAssignedAvatarIds}
                />

                {error && (
                    <div className="text-sm text-danger-500 bg-danger-100/60 border border-danger-500/30 px-3 py-2 rounded-md">
                        {error.message}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
                    <Button type="submit" variant="accent" disabled={submitting}>
                        {submitting ? t('members.creating') : t('members.create')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

function OneTimePasswordDialog({ data, onClose, hintKey = 'members.otpChangeHint' }) {
    const { t } = useTranslation('members');
    const [copied, setCopied] = useState(false);
    async function copy() {
        const ok = await copyText(data.password);
        setCopied(ok);
        if (ok) setTimeout(() => setCopied(false), 2000);
    }
    return (
        <Modal onClose={onClose} title={t('members.otpTitle')} wide>
            <p className="text-sm text-ink-700 leading-relaxed mb-4">
                <strong>{data.email}</strong> {t('members.otpDescPart1')}
                <span className="text-danger-500"> {t('members.otpDescWarning')}</span>
                {t('members.otpDescPart2')}
            </p>
            <div className="bg-ink-900 text-cream-50 px-4 py-3 rounded-md font-mono text-base flex items-center gap-3">
                <span className="flex-1 select-all break-all">{data.password}</span>
                <button onClick={copy} className="px-2 py-1 bg-ink-700 hover:bg-ink-500 rounded text-xs flex items-center gap-1">
                    <Copy size={12} /> {copied ? t('members.copied') : t('members.copy')}
                </button>
            </div>
            <div className="text-xs text-ink-500 mt-3">
                {t(hintKey)}
            </div>
            <div className="flex justify-end mt-5">
                <Button variant="primary" onClick={onClose}>{t('members.otpCloseButton')}</Button>
            </div>
        </Modal>
    );
}

function Modal({ title, children, onClose, wide }) {
    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center px-4">
            <div className={['bg-cream-50 border border-cream-200 rounded-lg shadow-xl p-6', wide ? 'w-full max-w-md' : 'w-full max-w-sm'].join(' ')}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="heading-display text-lg font-semibold text-ink-900">{title}</h2>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
                        <X size={18} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function QuotaPill({ label, used, total, fallback }) {
    const { t } = useTranslation('members');
    const hasQuota = typeof total === 'number';
    const usedNum = typeof used === 'number' ? used : null;
    const pct = hasQuota && total > 0 && usedNum != null ? Math.min(100, (usedNum / total) * 100) : 0;
    const isFull = hasQuota && usedNum != null && usedNum >= total;
    const isNear = hasQuota && usedNum != null && usedNum >= total * 0.8 && !isFull;
    const barColor = isFull ? 'bg-danger-500' : isNear ? 'bg-warn-500' : 'bg-accent-500';
    const textColor = isFull ? 'text-danger-500' : isNear ? 'text-warn-500' : 'text-ink-900';

    return (
        <div className="px-4 py-3 bg-cream-100 border border-cream-200 rounded-md">
            <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">{label}</div>
                {hasQuota && usedNum != null ? (
                    <div className={`text-sm font-medium tnum ${textColor}`}>
                        {usedNum} <span className="text-ink-400">/</span> {total}
                    </div>
                ) : (
                    <div className="text-xs text-ink-400">{t('members.noQuota')}</div>
                )}
            </div>
            {hasQuota && (
                <div className="h-1 bg-cream-200 rounded overflow-hidden">
                    <div className={`h-full ${barColor} transition-[width]`} style={{ width: `${pct}%` }} />
                </div>
            )}
            {!hasQuota && (
                <div className="text-xs text-ink-500">{fallback}</div>
            )}
        </div>
    );
}

/* ───────────────────────── 商用帳號（dealer 專屬分頁）───────────────────────── */

function BusinessAccountsTab() {
    const { t, i18n } = useTranslation('members');
    const { user } = useAuth();
    const { tenants } = useTenant();        // 經銷商自己的 tenant（複製角色的來源池）
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [cloneFor, setCloneFor] = useState(null);   // 要複製角色的 business row
    const [reportMonth, setReportMonth] = useState(currentMonthTaipei());   // 報表下載的結算月份（整列共用）
    const [pickTenantsFor, setPickTenantsFor] = useState(null);             // 多租戶帳號 → 先選要哪幾個租戶
    const [batchBusy, setBatchBusy] = useState(false);                      // 整批報表下載中（要鎖，這支要跑 20 秒以上）
    const [jobs, setJobs] = useState({});             // businessUid → { jobId, total, done, status } 背景 clone 進度
    const jobsRef = useRef(jobs);
    jobsRef.current = jobs;

    async function load() {
        setLoading(true);
        try {
            const r = await accountsApi.listBusiness();
            setList((r.parents || []).filter((p) => p.accountRole === 'business'));
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function downloadBatch() {
        setBatchBusy(true);
        try {
            await accountsApi.downloadDealerReport(user.uid, reportMonth, { lang: i18n.language });
        } catch (e) {
            alert(e.message);
        } finally {
            setBatchBusy(false);
        }
    }

    // 背景 clone 進度輪詢（modal 關了也繼續，進度顯示在該帳號那列）
    const activeKey = Object.entries(jobs)
        .filter(([, j]) => j && j.status !== 'done')
        .map(([uid, j]) => `${uid}:${j.jobId}`).sort().join(',');
    useEffect(() => {
        if (!activeKey) return undefined;
        let cancelled = false;
        const tick = async () => {
            const entries = Object.entries(jobsRef.current).filter(([, j]) => j && j.status !== 'done');
            for (const [uid, j] of entries) {
                try {
                    const d = await accountsApi.cloneJobStatus(j.jobId);
                    if (cancelled) return;
                    setJobs((prev) => (prev[uid] ? { ...prev, [uid]: { ...prev[uid], done: d.done, total: d.total, status: d.status } } : prev));
                    if (d.status === 'done') {
                        load();   // 更新 rolesUsed
                        setTimeout(() => { if (!cancelled) setJobs((prev) => { const n = { ...prev }; delete n[uid]; return n; }); }, 4000);
                    }
                } catch (_) { /* 下次再試 */ }
            }
        };
        tick();
        const id = setInterval(tick, 2000);
        return () => { cancelled = true; clearInterval(id); };
    }, [activeKey]);

    return (
        <div>
            <PageHeader
                eyebrow="dealer · business"
                title={t('members.tabBusiness')}
                description={t('members.biz.description')}
                actions={
                    <div className="flex items-center gap-2">
                        {/* 報表是按月結算的，月份選在這裡、整頁共用 —— 每列各放一個選擇器會很吵 */}
                        <MonthPager month={reportMonth} onChange={setReportMonth} />
                        {/* 整批報表 —— 旗下每個商用帳號一張分頁。這支要跑 20 秒以上（掃 Firestore），
                            所以 disabled + 轉圈，不然使用者會連按好幾次。 */}
                        <Button variant="outline" onClick={downloadBatch} disabled={batchBusy || !user?.uid || list.length === 0}
                            title={t('members.biz.batchReportTitle', { month: reportMonth })}>
                            {batchBusy
                                ? <><Loader2 size={14} className="animate-spin" /> {t('members.biz.batchReportBusy')}</>
                                : <><Download size={14} /> {t('members.biz.batchReport')}</>}
                        </Button>
                        <Button variant="accent" onClick={() => setShowCreate(true)}>
                            <Plus size={14} /> {t('members.biz.create')}
                        </Button>
                    </div>
                }
            />

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {showCreate && (
                <CreateAccountModal
                    asDealer
                    onClose={() => setShowCreate(false)}
                    onCreated={load}
                />
            )}

            {cloneFor && (
                <CloneRolesModal
                    business={cloneFor}
                    dealerTenants={tenants || []}
                    activeJob={jobs[cloneFor.uid]}
                    onJobStarted={(uid, j) => setJobs((prev) => ({ ...prev, [uid]: { ...j, done: 0, status: 'running' } }))}
                    onClose={() => setCloneFor(null)}
                    onDone={() => { setCloneFor(null); load(); }}
                />
            )}

            {pickTenantsFor && (
                <PickTenantsModal
                    biz={pickTenantsFor}
                    month={reportMonth}
                    onClose={() => setPickTenantsFor(null)}
                />
            )}

            {loading ? (
                <div className="text-ink-400 text-sm">{t('members.biz.loading')}</div>
            ) : list.length === 0 ? (
                <Card>
                    <Empty
                        icon={Building2}
                        title={t('members.biz.empty')}
                        description={t('members.biz.emptyDesc')}
                        action={
                            <Button variant="accent" onClick={() => setShowCreate(true)}>
                                <Plus size={14} /> {t('members.biz.create')}
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <div className="grid gap-2">
                    {list.map((b) => (
                        <BusinessRow key={b.uid} biz={b} job={jobs[b.uid]} month={reportMonth}
                            onClone={() => setCloneFor(b)} onDeleted={load}
                            onPickTenants={() => setPickTenantsFor(b)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BusinessRow({ biz, job, month, onClone, onDeleted, onPickTenants }) {
    const { t, i18n } = useTranslation('members');
    const rq = biz.roleQuota || {};
    const ru = biz.rolesUsed || {};
    const seats = biz.seats || null;
    const tenantsArr = biz.tenants || [];
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const fmt = (used, total) =>
        typeof total === 'number' ? `${used ?? 0}/${total}` : t('members.biz.unlimited');
    const running = job && job.status !== 'done';

    // 共用租戶的商用子帳號不擁有 tenant，報表走它自己的角色分區 → 不進勾選清單
    const ownTenants = tenantsArr.filter((x) => !x.sharedBusiness);

    async function downloadReport() {
        // 2 個以上租戶 → 先讓使用者勾要哪幾個；1 個（或 0，共用租戶）直接下載
        if (ownTenants.length > 1) { onPickTenants?.(); return; }
        setDownloading(true);
        try {
            await accountsApi.downloadReport(biz.uid, month, { lang: i18n.language });
        } catch (e) {
            alert(e.message);
        } finally {
            setDownloading(false);
        }
    }

    async function remove() {
        if (!confirm(t('members.biz.deleteConfirm', { email: biz.email }))) return;
        setDeleting(true);
        try {
            await accountsApi.deleteBusiness(biz.uid);
            onDeleted?.();
        } catch (e) {
            alert(e.message);
            setDeleting(false);
        }
    }

    return (
        <Card className="flex items-center gap-4">
            <div className="size-10 rounded-md grid place-items-center shrink-0 bg-accent-50 text-accent-700">
                <Briefcase size={18} strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-900 truncate flex items-center gap-2">
                    <span className="truncate">{biz.displayName || biz.email}</span>
                    {biz.sharedHostId && (
                        <span
                            className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full bg-accent-50 text-accent-700 border border-accent-300/60 font-mono text-[9px] uppercase tracking-[0.12em] leading-none"
                            title={t('members.biz.sharedTitle', { hostId: biz.sharedHostId })}
                        >
                            {t('members.biz.sharedBadge', { hostId: biz.sharedHostId })}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-ink-500 flex-wrap">
                    <span>{t('members.biz.aa')} <b className="tnum text-ink-700">{fmt(ru.plan_01, rq.plan_01)}</b></span>
                    <span>{t('members.biz.cs')} <b className="tnum text-ink-700">{fmt(ru.plan_02, rq.plan_02)}</b></span>
                    {/* 席次 ≠ 上面的角色數：席次決定免費分鐘（800×席次），角色數只是能開幾隻 AI。
                        對帳時看的是席次，這頁以前只顯示角色數 → 經銷商會拿錯數字跟客戶談。 */}
                    <span title={t('members.biz.seatsHint')}>
                        {t('members.biz.seats')} <b className="tnum text-ink-700">
                            {seats ? `AA ${seats.plan_01 ?? 0} · CS ${seats.plan_02 ?? 0}` : t('members.biz.unlimited')}
                        </b>
                    </span>
                    <span className="text-ink-300">·</span>
                    <span className="truncate">
                        {t('members.biz.tenants')}: {tenantsArr.length
                            ? tenantsArr.map((x) => (x.displayName && x.displayName !== x.hostId
                                ? `${x.displayName}（${x.hostId}）` : x.hostId)).join('、')
                            : t('members.biz.noTenant')}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={downloadReport} disabled={downloading}
                    title={t('members.biz.downloadReportTitle', { month })}>
                    {downloading
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Download size={13} />} {t('members.biz.downloadReport')}
                </Button>
                {job ? (
                    <span className={[
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium',
                        running ? 'bg-accent-50 text-accent-700' : 'bg-success-100 text-success-500',
                    ].join(' ')}>
                        {running
                            ? <><Loader2 size={12} className="animate-spin" /> {t('members.clone.cloning')} {job.total ? `${job.done}/${job.total}` : ''}</>
                            : <><Check size={12} /> {t('members.clone.done')}</>}
                    </span>
                ) : (
                    <Button size="sm" variant="ghost" onClick={onClone} disabled={!tenantsArr.length} title={t('members.biz.cloneRoles')}>
                        <CopyPlus size={13} /> {t('members.biz.cloneRoles')}
                    </Button>
                )}
                <Button size="sm" variant="ghost" onClick={remove} disabled={deleting || running} title={t('members.biz.delete')}>
                    {deleting ? <Loader2 size={13} className="animate-spin text-danger-500" /> : <Trash2 size={13} className="text-danger-500" />}
                </Button>
            </div>
        </Card>
    );
}

/**
 * 多租戶帳號下載報表前的租戶勾選。
 *
 * ⚠️ 勾選**只縮小明細範圍**：報表表頭的免費額度與超出分鐘一律以帳號全部租戶合計計算
 * （跟月帳單同一口徑）。不講清楚的話，客戶只勾一個租戶會看到「沒超額」，拿去質疑帳單。
 */
function PickTenantsModal({ biz, month, onClose }) {
    const { t, i18n } = useTranslation('members');
    const options = (biz.tenants || []).filter((x) => !x.sharedBusiness);
    const [picked, setPicked] = useState(() => options.map((x) => x.hostId));   // 預設全選
    const [busy, setBusy] = useState(false);

    const toggle = (hostId) => setPicked((p) => (
        p.includes(hostId) ? p.filter((x) => x !== hostId) : [...p, hostId]
    ));

    async function go() {
        setBusy(true);
        try {
            // 全選 = 不帶 hostIds（後端預設就是全部），少一個會變動的參數
            const all = picked.length === options.length;
            await accountsApi.downloadReport(biz.uid, month, {
                hostIds: all ? undefined : picked,
                lang: i18n.language,
            });
            onClose();
        } catch (e) {
            alert(e.message);
            setBusy(false);
        }
    }

    return (
        <Modal onClose={onClose} title={t('members.biz.pickTenants.title', { month })}>
            <p className="text-sm text-ink-500 mb-3">{t('members.biz.pickTenants.desc')}</p>
            <div className="space-y-1.5 mb-4">
                {options.map((x) => (
                    <label key={x.hostId} className="flex items-center gap-2.5 px-3 py-2 rounded-md border border-cream-200 hover:bg-cream-50 cursor-pointer">
                        <input type="checkbox" checked={picked.includes(x.hostId)} onChange={() => toggle(x.hostId)} />
                        <span className="text-sm text-ink-900">{x.displayName || x.hostId}</span>
                        <span className="font-mono text-[11px] text-ink-400">{x.hostId}</span>
                    </label>
                ))}
            </div>
            <p className="text-[11px] text-ink-400 leading-relaxed mb-4">{t('members.biz.pickTenants.note')}</p>
            <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose}>{t('members.biz.pickTenants.cancel')}</Button>
                <Button variant="accent" onClick={go} disabled={busy || picked.length === 0}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {t('members.biz.downloadReport')}
                </Button>
            </div>
        </Modal>
    );
}

/** 複製角色 modal — 從經銷商角色庫挑 → deep-copy 成新角色掛到商用帳號 tenant，前端先擋 AA/CS 額度 */
function CloneRolesModal({ business, dealerTenants, activeJob, onJobStarted, onClose, onDone }) {
    const { t } = useTranslation('members');
    const bizTenants = business.tenants || [];
    const [targetHostId, setTargetHostId] = useState(bizTenants[0]?.hostId || '');
    const [avatars, setAvatars] = useState([]);
    const [loadingAv, setLoadingAv] = useState(true);
    const [selected, setSelected] = useState(() => new Set());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [job, setJob] = useState(null);          // { jobId, total } — 背景 clone job
    const [progress, setProgress] = useState(null); // 輪詢回來的 { status, done, total, ... }

    // 背景 job 進度輪詢（不卡 UI，跑 loading 直到完成）
    useEffect(() => {
        if (!job || result) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const d = await accountsApi.cloneJobStatus(job.jobId);
                if (cancelled) return;
                setProgress(d);
                if (d.status === 'done') setResult(d);
            } catch (_) { /* 輪詢失敗下次再試 */ }
        };
        tick();
        const id = setInterval(tick, 2000);
        return () => { cancelled = true; clearInterval(id); };
    }, [job, result]);

    const rq = business.roleQuota || {};
    const ru = business.rolesUsed || {};
    const remain = (type) => (typeof rq[type] === 'number' ? Math.max(0, rq[type] - (ru[type] || 0)) : Infinity);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingAv(true);
            try {
                const lists = await Promise.all((dealerTenants || []).map((tn) =>
                    accountsApi.tenantAvatars(tn.hostId).then((r) => r.avatars || []).catch(() => [])
                ));
                if (cancelled) return;
                const seen = new Set();
                const out = [];
                for (const a of lists.flat()) {
                    if (!a.avatarId || seen.has(a.avatarId)) continue;
                    if (a.enabled !== true) continue;   // 只列已發佈(啟用)的角色
                    seen.add(a.avatarId);
                    // 用共用 resolveFlowMode（看 flow 實際節點），跟 AA/CS 配額計數同一套規則
                    const fm = resolveFlowMode(a);
                    out.push({
                        avatarId: a.avatarId,
                        name: a.name || a.avatarId,
                        hostId: a.hostId,
                        type: fm === 'transfer-only' ? 'plan_01' : 'plan_02',
                    });
                }
                out.sort((x, y) => (x.name || '').localeCompare(y.name || '', 'zh-TW'));
                setAvatars(out);
            } finally {
                if (!cancelled) setLoadingAv(false);
            }
        })();
        return () => { cancelled = true; };
    }, [dealerTenants]);

    const selCount = useMemo(() => {
        let aa = 0, cs = 0;
        for (const a of avatars) if (selected.has(a.avatarId)) (a.type === 'plan_01' ? (aa += 1) : (cs += 1));
        return { plan_01: aa, plan_02: cs };
    }, [avatars, selected]);

    const overAA = selCount.plan_01 > remain('plan_01');
    const overCS = selCount.plan_02 > remain('plan_02');
    const canSubmit = !!targetHostId && selected.size > 0 && !overAA && !overCS && !submitting;

    const toggle = (id) => {
        const n = new Set(selected);
        n.has(id) ? n.delete(id) : n.add(id);
        setSelected(n);
    };

    async function submit() {
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const r = await accountsApi.cloneAvatars(business.uid, {
                sourceAvatarIds: [...selected],
                targetHostId,
            });
            setJob(r);   // { jobId, total } → 切到背景進度視圖，開始輪詢
            onJobStarted?.(business.uid, { jobId: r.jobId, total: r.total });   // 提升到父層，關掉 modal 也看得到進度
        } catch (e) {
            setError(e);
        } finally {
            setSubmitting(false);
        }
    }

    const fmtRemain = (n) => (n === Infinity ? '∞' : n);

    return (
        <Modal onClose={onClose} title={t('members.clone.title', { name: business.displayName || business.email })} wide>
            {result ? (
                <div className="space-y-4">
                    <div className="rounded-lg border border-success-500/30 bg-success-100/40 px-4 py-3 text-sm text-ink-700">
                        <div className="font-medium text-success-500 mb-1">{t('members.clone.successTitle')}</div>
                        {t('members.clone.successDesc', { n: result.cloned ?? 0 })}
                        {(result.results || []).some((x) => !x.ok) && (
                            <ul className="mt-2 text-xs text-danger-500 list-disc pl-4">
                                {(result.results || []).filter((x) => !x.ok).map((x) => (
                                    <li key={x.sourceAvatarId}>{x.sourceAvatarId}: {x.error}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <Button variant="primary" onClick={onDone}>{t('members.clone.done')}</Button>
                    </div>
                </div>
            ) : job ? (
                <div className="space-y-4">
                    <div className="flex items-center gap-3 text-sm text-ink-700">
                        <Loader2 size={18} className="animate-spin text-accent-700" />
                        <span>{t('members.clone.cloning')} {progress ? `(${progress.done}/${progress.total})` : ''}</span>
                    </div>
                    <div className="h-1.5 bg-cream-200 rounded overflow-hidden">
                        <div
                            className="h-full bg-accent-500 transition-[width]"
                            style={{ width: progress && progress.total ? `${Math.round((progress.done / progress.total) * 100)}%` : '8%' }}
                        />
                    </div>
                    <p className="text-[11px] text-ink-400">{t('members.clone.background')}</p>
                    <div className="flex justify-end">
                        <Button variant="ghost" onClick={onDone}>{t('members.clone.runInBackground')}</Button>
                    </div>
                </div>
            ) : bizTenants.length === 0 ? (
                <div className="space-y-4">
                    <div className="text-sm text-warn-500">{t('members.clone.noTargetTenant')}</div>
                    <div className="flex justify-end">
                        <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <p className="text-xs text-ink-500 leading-relaxed">{t('members.clone.desc')}</p>

                    {/* 目標租戶 */}
                    {bizTenants.length > 1 ? (
                        <div>
                            <span className="block text-xs font-medium text-ink-700 mb-1.5 uppercase tracking-wide">
                                {t('members.clone.targetTenant')}
                            </span>
                            <select
                                value={targetHostId}
                                onChange={(e) => setTargetHostId(e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-white border border-cream-300 rounded-md focus:outline-none focus:border-accent-500"
                            >
                                {bizTenants.map((tn) => <option key={tn.hostId} value={tn.hostId}>{tn.hostId}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div className="text-xs text-ink-500">
                            {t('members.clone.targetTenant')}: <span className="font-mono text-ink-700">{targetHostId}</span>
                        </div>
                    )}

                    {/* 額度 / 已選 */}
                    <div className="flex items-center justify-between text-[11px] px-1">
                        <span className="text-ink-400">
                            {t('members.clone.remaining', { aa: fmtRemain(remain('plan_01')), cs: fmtRemain(remain('plan_02')) })}
                        </span>
                        <span className={(overAA || overCS) ? 'text-danger-500 font-medium' : 'text-ink-600'}>
                            {t('members.clone.selected', { aa: selCount.plan_01, cs: selCount.plan_02 })}
                        </span>
                    </div>

                    {/* 角色清單 */}
                    <div>
                        <span className="block text-xs font-medium text-ink-700 mb-1.5 uppercase tracking-wide">
                            {t('members.clone.source')}
                            <span className="ml-1.5 normal-case font-normal text-[10px] text-ink-400">{t('members.clone.publishedOnly')}</span>
                        </span>
                        <div className="border border-cream-300 rounded-md max-h-60 overflow-y-auto divide-y divide-cream-100">
                            {loadingAv ? (
                                <div className="px-3 py-6 text-center text-xs text-ink-400">{t('members.clone.loadingAvatars')}</div>
                            ) : avatars.length === 0 ? (
                                <div className="px-3 py-6 text-center text-xs text-ink-400">{t('members.clone.noAvatars')}</div>
                            ) : avatars.map((a) => {
                                const on = selected.has(a.avatarId);
                                const isAA = a.type === 'plan_01';
                                return (
                                    <button
                                        key={a.avatarId}
                                        type="button"
                                        onClick={() => toggle(a.avatarId)}
                                        className={['w-full flex items-center gap-2.5 px-3 py-2 text-left transition', on ? 'bg-accent-50' : 'hover:bg-cream-50'].join(' ')}
                                    >
                                        <span className={['size-4 rounded border grid place-items-center shrink-0', on ? 'bg-accent-700 border-accent-700 text-white' : 'border-cream-300 bg-white'].join(' ')}>
                                            {on && <Check size={12} strokeWidth={3} />}
                                        </span>
                                        <span className={['text-sm truncate flex-1', on ? 'text-ink-900 font-medium' : 'text-ink-700'].join(' ')}>
                                            {a.name}
                                        </span>
                                        <span className={['shrink-0 font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded', isAA ? 'bg-cream-100 text-ink-500' : 'bg-accent-50 text-accent-700'].join(' ')}>
                                            {isAA ? 'AA' : 'CS'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {(overAA || overCS) && <div className="text-[11px] text-danger-500 mt-1.5">{t('members.clone.exceed')}</div>}
                    </div>

                    {error && (
                        <div className="text-sm text-danger-500 bg-danger-100/60 border border-danger-500/30 px-3 py-2 rounded-md">
                            {error.message}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
                        <Button type="button" variant="accent" disabled={!canSubmit} onClick={submit}>
                            {submitting ? <><Loader2 size={14} className="animate-spin" /> {t('members.clone.cloning')}</> : <><CopyPlus size={14} /> {t('members.clone.submit')}</>}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

/* ───────────────────────── 真人客服（handoff agents）───────────────────────── */

function ServiceAgentsTab() {
    const { t } = useTranslation('members');
    const { hostId, currentTenant, isAdmin } = useTenant();

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [editAgent, setEditAgent] = useState(null);
    const [showOTP, setShowOTP] = useState(null);   // { email, password }

    async function load() {
        if (!hostId) return;
        setLoading(true);
        try {
            const r = await serviceAgentsApi.list(hostId);
            setList(r.agents || []);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    if (!isAdmin) {
        return (
            <Card>
                <Empty icon={Headphones} title={t('members.adminOnlyTitle')} description={t('members.adminOnlyDesc')} />
            </Card>
        );
    }

    return (
        <div className="max-w-4xl">
            <PageHeader
                eyebrow={`tenant · ${hostId}`}
                title={t('members.agents.title')}
                description={t('members.agents.description', { tenant: currentTenant?.displayName || hostId })}
                actions={
                    <Button variant="accent" onClick={() => setShowCreate(true)}>
                        <Plus size={14} /> {t('members.agents.add')}
                    </Button>
                }
            />

            <div className="mb-6 px-4 py-3 bg-cream-100 border border-cream-200 rounded-md text-xs text-ink-500 leading-relaxed">
                {t('members.agents.intro')}
            </div>

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {showCreate && (
                <CreateAgentDialog
                    hostId={hostId}
                    onClose={() => setShowCreate(false)}
                    onCreated={(r) => {
                        setShowCreate(false);
                        load();
                        if (r.oneTimePassword) setShowOTP({ email: r.agent.email, password: r.oneTimePassword });
                    }}
                />
            )}
            {editAgent && (
                <EditAgentDialog
                    hostId={hostId}
                    agent={editAgent}
                    onClose={() => setEditAgent(null)}
                    onSaved={() => { setEditAgent(null); load(); }}
                />
            )}
            {showOTP && <OneTimePasswordDialog data={showOTP} hintKey="members.agents.otpChangeHint" onClose={() => setShowOTP(null)} />}

            {loading ? (
                <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>
            ) : list.length === 0 ? (
                <Card>
                    <Empty
                        icon={Headphones}
                        title={t('members.agents.emptyTitle')}
                        description={t('members.agents.emptyDesc')}
                        action={
                            <Button variant="accent" onClick={() => setShowCreate(true)}>
                                <Plus size={14} /> {t('members.agents.add')}
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <div className="grid gap-2">
                    {list.map((a) => (
                        <AgentRow
                            key={a.uid}
                            agent={a}
                            hostId={hostId}
                            onEdit={() => setEditAgent(a)}
                            onChange={load}
                            onOTP={(data) => setShowOTP(data)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function AgentRow({ agent, hostId, onEdit, onChange, onOTP }) {
    const { t } = useTranslation('members');
    const online = agent.status === 'online';
    const channels = agent.channels || [];

    async function remove() {
        if (!confirm(t('members.agents.removeConfirm', { email: agent.email }))) return;
        try {
            await serviceAgentsApi.remove(hostId, agent.uid);
            onChange();
        } catch (e) {
            alert(e.message);
        }
    }

    async function resetPassword() {
        if (!confirm(t('members.agents.resetPwConfirm', { email: agent.email }))) return;
        try {
            const r = await serviceAgentsApi.resetPassword(hostId, agent.uid);
            onOTP({ email: r.email, password: r.oneTimePassword });
        } catch (e) {
            alert(e.message);
        }
    }

    return (
        <Card className="flex items-center gap-4">
            <div className="size-10 rounded-md grid place-items-center shrink-0 bg-accent-50 text-accent-700 relative overflow-visible">
                {agent.avatarUrl
                    ? <img src={agent.avatarUrl} alt="" className="size-10 rounded-md object-cover" />
                    : <Headphones size={18} strokeWidth={1.6} />}
                <span
                    className={['absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-white', online ? 'bg-success-500' : 'bg-cream-300'].join(' ')}
                    title={online ? t('members.agents.online') : t('members.agents.offline')}
                />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink-900">{agent.displayName || agent.email}</span>
                    <span className={['text-[10px] font-medium px-1.5 py-0.5 rounded-full', online ? 'bg-success-100 text-success-500' : 'bg-cream-100 text-ink-400'].join(' ')}>
                        {online ? t('members.agents.online') : t('members.agents.offline')}
                    </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-ink-500">{agent.email}</span>
                    <span className="text-[10px] font-mono text-ink-300">{(agent.uid || '').slice(0, 12)}…</span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-ink-400 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                        <Phone size={11} />
                        {agent.extension
                            ? t('members.agents.extMode', { ext: agent.extension })
                            : t('members.agents.browserMode')}
                    </span>
                    <span className="text-ink-300">·</span>
                    <span className="inline-flex items-center gap-1">
                        <Globe size={11} />
                        {channels.length
                            ? channels.map((c) => t(`members.agents.channel.${c}`)).join(' + ')
                            : t('members.agents.noChannel')}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={onEdit} title={t('common:actions.edit')}>
                    <Pencil size={13} className="text-ink-500" />
                </Button>
                <Button size="sm" variant="ghost" onClick={resetPassword} title={t('members.agents.resetPw')}>
                    <KeyRound size={13} className="text-ink-500" />
                </Button>
                <Button size="sm" variant="ghost" onClick={remove} title={t('common:actions.delete')}>
                    <Trash2 size={13} className="text-danger-500" />
                </Button>
            </div>
        </Card>
    );
}

function AgentFormFields({ form, setForm, emailEditable }) {
    const { t } = useTranslation('members');
    const toggleChannel = (c) => {
        const next = new Set(form.channels);
        next.has(c) ? next.delete(c) : next.add(c);
        setForm({ ...form, channels: [...next] });
    };
    return (
        <>
            <Input
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="agent@your-company.com"
                required
                disabled={!emailEditable}
                hint={emailEditable ? t('members.agents.emailHint') : undefined}
            />
            <Input
                label={t('members.displayName')}
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder={t('members.agents.displayNamePlaceholder')}
            />
            <Input
                label={t('members.agents.extension')}
                value={form.extension}
                onChange={(e) => setForm({ ...form, extension: e.target.value.replace(/[^\d]/g, '') })}
                placeholder="327 / 0912345678"
                hint={t('members.agents.extensionHint')}
            />
            <div>
                <span className="block text-xs font-medium text-ink-700 mb-1.5 uppercase tracking-wide">
                    {t('members.agents.channels')}
                </span>
                <div className="grid grid-cols-2 gap-2">
                    {['web', 'phone'].map((c) => {
                        const on = form.channels.includes(c);
                        return (
                            <button
                                key={c}
                                type="button"
                                onClick={() => toggleChannel(c)}
                                className={[
                                    'px-3 py-2 rounded-md border text-left transition',
                                    on ? 'border-accent-500 bg-accent-50' : 'border-cream-300 bg-white hover:bg-cream-100',
                                ].join(' ')}
                            >
                                <div className="text-sm font-medium text-ink-900">{t(`members.agents.channel.${c}`)}</div>
                                <div className="text-[10px] text-ink-500 mt-0.5">{t(`members.agents.channelDesc.${c}`)}</div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

function CreateAgentDialog({ hostId, onClose, onCreated }) {
    const { t } = useTranslation('members');
    const [form, setForm] = useState({ email: '', displayName: '', extension: '', channels: ['web', 'phone'] });
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const pwTooShort = password.length > 0 && password.length < 6;

    async function submit(e) {
        e.preventDefault();
        if (pwTooShort) return;
        setSubmitting(true);
        setError(null);
        try {
            const r = await serviceAgentsApi.create(hostId, { ...form, ...(password ? { password } : {}) });
            onCreated(r);
        } catch (err) {
            setError(err);
            setSubmitting(false);
        }
    }

    return (
        <Modal onClose={onClose} title={t('members.agents.add')} wide>
            <form onSubmit={submit} className="space-y-4">
                <AgentFormFields form={form} setForm={setForm} emailEditable />
                <Input
                    label={t('members.agents.password')}
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('members.agents.passwordPlaceholder')}
                    hint={pwTooShort ? t('members.agents.passwordTooShort') : t('members.agents.passwordHint')}
                />
                {error && (
                    <div className="text-sm text-danger-500 bg-danger-100/60 border border-danger-500/30 px-3 py-2 rounded-md">
                        {error.message}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
                    <Button type="submit" variant="accent" disabled={submitting || !form.channels.length || pwTooShort}>
                        {submitting ? t('members.creating') : t('members.create')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

// 選檔 → 置中裁成正方形 → 縮到 256×256 PNG dataURL（LINE sender icon 用不到更大）
async function cropToSquarePng(file, size = 256) {
    const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error('unreadable image'));
        i.src = URL.createObjectURL(file);
    });
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - s) / 2;
    const sy = (img.naturalHeight - s) / 2;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    c.getContext('2d').drawImage(img, sx, sy, s, s, 0, 0, size, size);
    URL.revokeObjectURL(img.src);
    return c.toDataURL('image/png');
}

// 頭像上傳（編輯視窗內）：即選即傳，LINE 接手時專員訊息顯示此頭像；沒上傳用系統預設 icon
function AgentAvatarField({ hostId, agent, onChanged }) {
    const { t } = useTranslation('members');
    const [busy, setBusy] = useState(false);
    const [url, setUrl] = useState(agent.avatarUrl || '');
    const [err, setErr] = useState('');
    const fileRef = useRef(null);

    async function pick(e) {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true); setErr('');
        try {
            const dataUrl = await cropToSquarePng(file);
            const r = await serviceAgentsApi.uploadAvatar(hostId, agent.uid, dataUrl);
            setUrl(r.avatarUrl || '');
            onChanged && onChanged();
        } catch (e2) { setErr(e2.message); }
        finally { setBusy(false); }
    }
    async function remove() {
        setBusy(true); setErr('');
        try {
            await serviceAgentsApi.removeAvatar(hostId, agent.uid);
            setUrl('');
            onChanged && onChanged();
        } catch (e2) { setErr(e2.message); }
        finally { setBusy(false); }
    }

    return (
        <div>
            <span className="block text-xs font-medium text-ink-700 mb-1.5 uppercase tracking-wide">
                {t('members.agents.avatar')}
            </span>
            <div className="flex items-center gap-3">
                <div className="size-14 rounded-full grid place-items-center bg-accent-50 text-accent-700 overflow-hidden shrink-0 border border-cream-200">
                    {url ? <img src={url} alt="" className="size-14 object-cover" /> : <Headphones size={22} strokeWidth={1.6} />}
                </div>
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
                            {busy ? t('members.agents.avatarUploading') : t('members.agents.avatarUpload')}
                        </Button>
                        {url && (
                            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove}>
                                <span className="text-danger-500">{t('members.agents.avatarRemove')}</span>
                            </Button>
                        )}
                    </div>
                    <span className="text-[10px] text-ink-400 leading-relaxed">{t('members.agents.avatarHint')}</span>
                    {err && <span className="text-[11px] text-danger-500">{err}</span>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} />
            </div>
        </div>
    );
}

function EditAgentDialog({ hostId, agent, onClose, onSaved }) {
    const { t } = useTranslation('members');
    const [form, setForm] = useState({
        email: agent.email || '',
        displayName: agent.displayName || '',
        extension: agent.extension || '',
        channels: agent.channels || ['web', 'phone'],
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [avatarChanged, setAvatarChanged] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await serviceAgentsApi.update(hostId, agent.uid, {
                displayName: form.displayName,
                extension: form.extension,
                channels: form.channels,
            });
            onSaved();
        } catch (err) {
            setError(err);
            setSubmitting(false);
        }
    }

    return (
        <Modal onClose={() => (avatarChanged ? onSaved() : onClose())} title={t('members.agents.editTitle', { name: agent.displayName || agent.email })} wide>
            <form onSubmit={submit} className="space-y-4">
                <AgentAvatarField hostId={hostId} agent={agent} onChanged={() => setAvatarChanged(true)} />
                <AgentFormFields form={form} setForm={setForm} emailEditable={false} />
                {error && (
                    <div className="text-sm text-danger-500 bg-danger-100/60 border border-danger-500/30 px-3 py-2 rounded-md">
                        {error.message}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="ghost" onClick={() => (avatarChanged ? onSaved() : onClose())}>{t('common:actions.cancel')}</Button>
                    <Button type="submit" variant="accent" disabled={submitting || !form.channels.length}>
                        {submitting ? t('members.saving') : t('common:actions.save')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
